import { useAudio } from "@/contexts/audio-context";
import { useEffect, useMemo, useRef } from "react";

type TimeUpdate = { seconds: number; duration: number };

type BunnyPlayer = {
    on(event: "ready", callback: () => void): void;
    on(event: "timeupdate", callback: (data: TimeUpdate | string) => void): void;
    off(event: "ready" | "timeupdate"): void;
    setCurrentTime(seconds: number): void;
};

type PlayerWindow = Window &
    typeof globalThis & {
        playerjs?: {
            Player: new (iframe: HTMLIFrameElement) => BunnyPlayer;
        };
    };

const PLAYER_SCRIPT_ID = "bunny-playerjs";

function loadPlayerJs(): Promise<void> {
    if ((window as PlayerWindow).playerjs) return Promise.resolve();

    return new Promise((resolve, reject) => {
        const existing = document.getElementById(PLAYER_SCRIPT_ID) as HTMLScriptElement | null;
        const script = existing ?? document.createElement("script");
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener("error", () => reject(new Error("Unable to load Bunny Player API")), { once: true });
        if (!existing) {
            script.id = PLAYER_SCRIPT_ID;
            script.src = "https://assets.mediadelivery.net/playerjs/player-0.1.0.min.js";
            document.head.appendChild(script);
        }
    });
}

function embedUrl(value: string): string {
    const url = new URL(value);
    url.searchParams.set("autoplay", "false");
    return url.toString();
}

function readSeconds(raw: TimeUpdate | string): number | null {
    try {
        const update = typeof raw === "string" ? (JSON.parse(raw) as TimeUpdate) : raw;
        return Number.isFinite(update.seconds) ? update.seconds : null;
    } catch {
        return null;
    }
}

export function VideoPlayer({ videoUrl }: { videoUrl: string }) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const playerRef = useRef<BunnyPlayer>();
    const playerTimeRef = useRef(0);
    const fromPlayerRef = useRef(false);
    const { currentTime, setCurrentTime } = useAudio();
    const setCurrentTimeRef = useRef(setCurrentTime);
    const currentTimeRef = useRef(currentTime);
    setCurrentTimeRef.current = setCurrentTime;
    currentTimeRef.current = currentTime;
    const src = useMemo(() => embedUrl(videoUrl), [videoUrl]);

    useEffect(() => {
        let cancelled = false;

        loadPlayerJs().then(() => {
            const Player = (window as PlayerWindow).playerjs?.Player;
            if (cancelled || !iframeRef.current || !Player) return;
            const player = new Player(iframeRef.current);
            playerRef.current = player;
            player.on("ready", () => {
                const target = currentTimeRef.current;
                if (target <= 0) return;
                playerTimeRef.current = target;
                player.setCurrentTime(target);
            });
            player.on("timeupdate", raw => {
                const seconds = readSeconds(raw);
                if (seconds === null) return;
                playerTimeRef.current = seconds;
                // Same 0.1s gate as AudioProvider: a smaller tick does not render,
                // so arming the latch here would swallow the next transcript click.
                if (Math.abs(seconds - currentTimeRef.current) <= 0.1) return;
                fromPlayerRef.current = true;
                setCurrentTimeRef.current(seconds);
            });
        });

        return () => {
            cancelled = true;
            playerRef.current?.off("ready");
            playerRef.current?.off("timeupdate");
            playerRef.current = undefined;
        };
    }, [src]);

    useEffect(() => {
        if (fromPlayerRef.current) {
            fromPlayerRef.current = false;
            return;
        }
        const player = playerRef.current;
        if (!player || Math.abs(currentTime - playerTimeRef.current) < 0.05) return;
        playerTimeRef.current = currentTime;
        player.setCurrentTime(currentTime);
    }, [currentTime]);

    return (
        <iframe
            ref={iframeRef}
            src={src}
            className="aspect-video w-full rounded-md border-0"
            allow="accelerometer; gyroscope; autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Call recording"
        />
    );
}
